import React, { useState, useEffect, useRef, useMemo } from 'react';
import { NominaService } from '../../services/nomina/nomina.service';
import { UsersService } from '../../services/users/users.service';
import toast from 'react-hot-toast';
import {
  X, Plus, Trash2, Banknote, Calculator, Calendar, User,
  CheckCircle, Loader2, AlertCircle, TrendingUp, TrendingDown,
  Clock, BadgePercent, ArrowDownCircle, ArrowUpCircle, Sun, PartyPopper
} from 'lucide-react';

interface Props {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

interface NovedadForm {
  tipo: 'FALTA' | 'HORA_EXTRA' | 'BONIFICACION' | 'DESCUENTO_OTRO' | 'FESTIVO';
  descripcion: string;
  cantidad: number;
  valorUnitario: number;
  fecha: string;
}

/* ─── helpers de cálculo ─── */
const salarioDiario = (sm: number) => sm / 30;
const valorHoraExtra = (sm: number) => (salarioDiario(sm) / 8) * 1.25;
const SALARIO_BASE_MENSUAL = 2_200_000;
const SALARIO_BASE_QUINCENAL = 1_100_000;

const formatCurrency = (amount: number) =>
  new Intl.NumberFormat('es-CO', { style: 'currency', currency: 'COP', minimumFractionDigits: 0 }).format(amount);

const tipoNovedadConfig: Record<NovedadForm['tipo'], { label: string; color: string; bg: string; icon: React.ReactNode }> = {
  FALTA:            { label: 'Falta',            color: 'text-red-700',      bg: 'bg-red-100',      icon: <AlertCircle className="w-3 h-3" /> },
  HORA_EXTRA:       { label: 'Hora extra',       color: 'text-blue-700',     bg: 'bg-blue-100',     icon: <Clock className="w-3 h-3" /> },
  BONIFICACION:     { label: 'Bonificación',     color: 'text-emerald-700',  bg: 'bg-emerald-100',  icon: <TrendingUp className="w-3 h-3" /> },
  FESTIVO:          { label: 'Festivo',          color: 'text-amber-700',    bg: 'bg-amber-100',    icon: <PartyPopper className="w-3 h-3" /> },
  DESCUENTO_OTRO:   { label: 'Descuento',        color: 'text-rose-700',     bg: 'bg-rose-100',     icon: <TrendingDown className="w-3 h-3" /> },
};

/* ─── Custom Select ─── */
interface CustomSelectProps {
  options: { value: string; label: string }[];
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  className?: string;
}

const CustomSelect: React.FC<CustomSelectProps> = ({ options, value, onChange, placeholder, className }) => {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const selected = options.find(o => o.value === value);

  useEffect(() => {
    const handle = (e: MouseEvent) => { if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false); };
    document.addEventListener('mousedown', handle);
    return () => document.removeEventListener('mousedown', handle);
  }, []);

  return (
    <div ref={ref} className={`relative ${className || ''}`}>
      <button
        type="button"
        onClick={() => setOpen(!open)}
        className="w-full text-left px-4 py-2.5 rounded-lg border border-gray-300 transition-colors outline-none focus:border-gray-500 cursor-pointer bg-white text-gray-900 hover:border-gray-400"
      >
        <span className={selected ? '' : 'text-gray-400'}>{selected ? selected.label : placeholder || 'Seleccionar'}</span>
      </button>
      {open && (
        <div className="absolute z-50 w-full mt-1 rounded-lg shadow-xl border max-h-60 overflow-y-auto bg-white border-gray-200">
          {options.map((opt) => (
            <button
              key={opt.value}
              type="button"
              onClick={() => { onChange(opt.value); setOpen(false); }}
              className={`w-full text-left px-4 py-2 text-sm transition-colors ${
                opt.value === value ? 'bg-emerald-50 text-emerald-700 font-medium' : 'text-gray-700 hover:bg-emerald-50'
              }`}
            >
              {opt.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export const CreateNominaDrawer: React.FC<Props> = ({ isOpen, onClose, onSuccess }) => {
  const [isVisible, setIsVisible] = useState(false);
  const [usuarios, setUsuarios] = useState<{ id: string; nombreCompleto: string; email: string }[]>([]);
  const [usuarioId, setUsuarioId] = useState('');
  const [periodoInicio, setPeriodoInicio] = useState('');
  const [periodoFin, setPeriodoFin] = useState('');
  const [salarioMensual, setSalarioMensual] = useState<number | undefined>(SALARIO_BASE_MENSUAL);
  const [diasLaborados, setDiasLaborados] = useState<number | undefined>(15);
  const [domingosTrabajados, setDomingosTrabajados] = useState<number | undefined>(2);
  const [novedades, setNovedades] = useState<NovedadForm[]>([]);
  const [observaciones, setObservaciones] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  /* carga de usuarios */
  useEffect(() => {
    if (isOpen) {
      setIsVisible(true);
      UsersService.getUsers(1, 100).then(res => {
        setUsuarios(res.data.filter((u: any) => u.tipoUsuario !== 'cliente'));
      });
    } else {
      setIsVisible(false);
    }
  }, [isOpen]);

  /* reset al abrir */
  useEffect(() => {
    if (isOpen) {
      setErrorMessage(null);
      // Asegurar que el salario base siempre tenga un valor válido
      setSalarioMensual(prev => prev || SALARIO_BASE_MENSUAL);
      setDiasLaborados(prev => prev || 15);
    }
  }, [isOpen]);

  /* calcular días laborados automáticamente según fechas (zona Colombia) */
  useEffect(() => {
    if (periodoInicio && periodoFin) {
      const inicio = new Date(`${periodoInicio}T00:00:00-05:00`);
      const fin = new Date(`${periodoFin}T00:00:00-05:00`);
      const diffTime = fin.getTime() - inicio.getTime();
      const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24)) + 1;
      if (diffDays > 0) {
        setDiasLaborados(diffDays);
      }
    }
  }, [periodoInicio, periodoFin]);

  /* El salario mensual es fijo en 2.200.000; los domingos son solo trazabilidad */

  const addNovedad = () => {
    setNovedades(prev => [...prev, {
      tipo: 'FALTA',
      descripcion: '',
      cantidad: 1,
      valorUnitario: salarioDiario(salarioMensual || 0),
      fecha: periodoInicio || new Date().toISOString().split('T')[0]
    }]);
  };

  const removeNovedad = (idx: number) => {
    setNovedades(prev => prev.filter((_, i) => i !== idx));
  };

  const updateNovedad = (idx: number, field: keyof NovedadForm, value: any) => {
    setNovedades(prev => {
      const next = [...prev];
      next[idx] = { ...next[idx], [field]: value };
      if (field === 'tipo') {
        if (value === 'FALTA') next[idx].valorUnitario = salarioDiario(salarioMensual || 0);
        else if (value === 'HORA_EXTRA') next[idx].valorUnitario = valorHoraExtra(salarioMensual || 0);
        else next[idx].valorUnitario = 0;
      }
      return next;
    });
  };

  const resumen = useMemo(() => {
    const sm = salarioMensual || SALARIO_BASE_MENSUAL;
    const dias = diasLaborados || 15;
    // Base proporcional a los días laborados (salario mensual / 30 × días)
    const base = (sm / 30) * dias;
    const totalNovedades = novedades.reduce((sum, n) => {
      const total = (n.valorUnitario || 0) * (n.cantidad || 0);
      if (n.tipo === 'FALTA' || n.tipo === 'DESCUENTO_OTRO') return { ...sum, descuento: sum.descuento + total };
      return { ...sum, bonificacion: sum.bonificacion + total };
    }, { descuento: 0, bonificacion: 0 });
    const devengado = base + totalNovedades.bonificacion;
    const totalPagar = devengado - totalNovedades.descuento;
    return { base, devengado, descuento: totalNovedades.descuento, bonificacion: totalNovedades.bonificacion, totalPagar };
  }, [salarioMensual, diasLaborados, novedades]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!usuarioId || !periodoInicio || !periodoFin) {
      toast.error('Completa todos los campos obligatorios');
      return;
    }
    setIsLoading(true);
    setErrorMessage(null);
    try {
      await NominaService.create({
        usuarioId,
        periodoInicio,
        periodoFin,
        salarioMensual: salarioMensual || 0,
        diasLaborados,
        domingosTrabajados,
        novedades: novedades.map(n => ({
          tipo: n.tipo,
          descripcion: n.descripcion,
          cantidad: Number(n.cantidad),
          valorUnitario: Number(n.valorUnitario),
          fecha: n.fecha,
        })),
        observaciones,
      });
      toast.success('Quincena creada exitosamente');
      onSuccess();
    } catch (err: any) {
      setErrorMessage(err.message || 'Error al crear quincena');
      toast.error(err.message || 'Error al crear quincena');
    } finally {
      setIsLoading(false);
    }
  };

  const handleClose = () => {
    setIsVisible(false);
    setTimeout(onClose, 300);
  };

  if (!isOpen) return null;

  return (
    <>
      <div className={`fixed inset-0 z-40 transition-opacity duration-300 ${isVisible ? 'opacity-100' : 'opacity-0'}`} onClick={handleClose} />
      <div className={`fixed right-0 top-20 h-[calc(100vh-5rem)] w-full max-w-6xl bg-gray-50 shadow-2xl z-40 transform transition-transform duration-300 ease-in-out ${isVisible ? 'translate-x-0' : 'translate-x-full'}`}>
        <div className="h-full flex flex-col">

          {/* Header */}
          <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 bg-white">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-emerald-100 rounded-lg">
                <Banknote className="w-5 h-5 text-emerald-600" />
              </div>
              <div>
                <h2 className="text-lg font-semibold text-gray-900">Nueva Quincena</h2>
                <p className="text-sm text-gray-500">Calcula el pago del colaborador</p>
              </div>
            </div>
            <button onClick={handleClose} className="p-2 hover:bg-gray-100 rounded-lg transition-colors">
              <X className="w-5 h-5 text-gray-500" />
            </button>
          </div>

          {/* Content */}
          <div className="flex-1 overflow-y-auto p-6">
            <form id="nomina-form" onSubmit={handleSubmit} className="space-y-6">

              {errorMessage && (
                <div className="p-4 bg-red-50 border border-red-200 rounded-xl flex items-start gap-3">
                  <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
                  <p className="text-sm text-red-700">{errorMessage}</p>
                </div>
              )}

              <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">

                {/* ─── Columna izquierda (2/3) ─── */}
                <div className="lg:col-span-2 space-y-6">

                  {/* Colaborador y Periodo */}
                  <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-5 space-y-4">
                    <div className="flex items-center gap-2 mb-1">
                      <User className="w-4 h-4 text-emerald-500" />
                      <h3 className="text-sm font-bold text-gray-900 uppercase tracking-wider">Colaborador y Periodo</h3>
                    </div>

                    <div>
                      <label className="block text-xs font-semibold text-gray-700 mb-1.5">
                        Colaborador <span className="text-red-500">*</span>
                      </label>
                      <CustomSelect
                        options={usuarios.map(u => ({ value: u.id, label: u.nombreCompleto }))}
                        value={usuarioId}
                        onChange={setUsuarioId}
                        placeholder="Seleccionar colaborador..."
                        className={!usuarioId && errorMessage ? 'border-red-500' : ''}
                      />
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-xs font-semibold text-gray-700 mb-1.5">Inicio periodo <span className="text-red-500">*</span></label>
                        <input type="date" value={periodoInicio} onChange={(e) => setPeriodoInicio(e.target.value)} className="w-full px-4 py-2.5 rounded-lg border border-gray-300 text-sm bg-white focus:border-gray-500 transition-colors" />
                      </div>
                      <div>
                        <label className="block text-xs font-semibold text-gray-700 mb-1.5">Fin periodo <span className="text-red-500">*</span></label>
                        <input type="date" value={periodoFin} onChange={(e) => setPeriodoFin(e.target.value)} className="w-full px-4 py-2.5 rounded-lg border border-gray-300 text-sm bg-white focus:border-gray-500 transition-colors" />
                      </div>
                    </div>
                  </div>

                  {/* Condiciones Laborales */}
                  <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-5 space-y-4">
                    <div className="flex items-center gap-2 mb-1">
                      <BadgePercent className="w-4 h-4 text-emerald-500" />
                      <h3 className="text-sm font-bold text-gray-900 uppercase tracking-wider">Condiciones Laborales</h3>
                    </div>

                    <div className="grid grid-cols-3 gap-4">
                      <div>
                        <label className="block text-xs font-semibold text-gray-700 mb-1.5">Salario mensual</label>
                        <input
                          type="text"
                          inputMode="numeric"
                          value={salarioMensual !== undefined ? salarioMensual.toLocaleString('en-US') : ''}
                          onChange={(e) => {
                            const raw = e.target.value.replace(/\D/g, '');
                            setSalarioMensual(raw === '' ? undefined : Number(raw));
                          }}
                          className="w-full px-4 py-2.5 rounded-lg border border-gray-300 text-sm bg-white focus:border-gray-500 transition-colors"
                        />
                        <p className="text-[10px] text-gray-400 mt-0.5">Base fija: $2.200.000</p>
                      </div>
                      <div>
                        <label className="block text-xs font-semibold text-gray-700 mb-1.5">Días laborados</label>
                        <input type="number" min={0} max={30} value={diasLaborados ?? ''} onChange={(e) => setDiasLaborados(e.target.value === '' ? undefined : Number(e.target.value))} className="w-full px-4 py-2.5 rounded-lg border border-gray-300 text-sm bg-white focus:border-gray-500 transition-colors" />
                        <p className="text-[10px] text-gray-400 mt-0.5">Solo trazabilidad</p>
                      </div>
                      <div>
                        <label className="block text-xs font-semibold text-gray-700 mb-1.5">Domingos trabajados</label>
                        <input type="number" min={0} max={10} value={domingosTrabajados ?? ''} onChange={(e) => setDomingosTrabajados(e.target.value === '' ? undefined : Number(e.target.value))} className="w-full px-4 py-2.5 rounded-lg border border-gray-300 text-sm bg-white focus:border-gray-500 transition-colors" />
                        <p className="text-[10px] text-gray-400 mt-0.5">Solo trazabilidad</p>
                      </div>
                    </div>

                    {/* Mini stats de condiciones */}
                    <div className="grid grid-cols-3 gap-3 pt-2">
                      <div className="p-3 bg-gray-50 rounded-lg border border-gray-100 text-center">
                        <p className="text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-1">Base quincenal</p>
                        <p className="text-sm font-bold text-gray-900">{formatCurrency(resumen.base)}</p>
                      </div>
                      <div className="p-3 bg-gray-50 rounded-lg border border-gray-100 text-center">
                        <p className="text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-1">Valor día</p>
                        <p className="text-sm font-bold text-gray-900">{formatCurrency(salarioDiario(salarioMensual || 0))}</p>
                      </div>
                      <div className="p-3 bg-gray-50 rounded-lg border border-gray-100 text-center">
                        <p className="text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-1">Hora extra</p>
                        <p className="text-sm font-bold text-gray-900">{formatCurrency(valorHoraExtra(salarioMensual || 0))}</p>
                      </div>
                    </div>
                  </div>

                  {/* Novedades */}
                  <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-5 space-y-4">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <Calculator className="w-4 h-4 text-emerald-500" />
                        <h3 className="text-sm font-bold text-gray-900 uppercase tracking-wider">Novedades</h3>
                      </div>
                      <button type="button" onClick={addNovedad} className="flex items-center gap-1 px-3 py-1.5 text-xs bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 font-medium transition-colors">
                        <Plus className="w-3.5 h-3.5" /> Agregar
                      </button>
                    </div>

                    {novedades.length === 0 && (
                      <div className="p-8 bg-gray-50/50 rounded-xl border border-dashed border-gray-200 text-center">
                        <Calculator className="w-10 h-10 text-gray-300 mx-auto mb-2" />
                        <p className="text-sm text-gray-500 font-medium">No hay novedades registradas</p>
                        <p className="text-xs text-gray-400 mt-0.5">Agrega faltas, horas extra, bonificaciones o festivos</p>
                      </div>
                    )}

                    <div className="space-y-3">
                      {novedades.map((n, idx) => {
                        const cfg = tipoNovedadConfig[n.tipo];
                        return (
                          <div key={idx} className="p-4 rounded-xl border border-gray-200 bg-white shadow-sm space-y-3">
                            <div className="flex items-center justify-between">
                              <div className="flex items-center gap-2">
                                <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-md text-[10px] font-bold uppercase tracking-wider ${cfg.bg} ${cfg.color}`}>
                                  {cfg.icon}
                                  {cfg.label}
                                </span>
                                <span className="text-xs text-gray-400 font-medium">Novedad {idx + 1}</span>
                              </div>
                              <button type="button" onClick={() => removeNovedad(idx)} className="p-1.5 text-red-500 hover:bg-red-50 rounded-lg transition-colors">
                                <Trash2 className="w-4 h-4" />
                              </button>
                            </div>

                            <div className="grid grid-cols-4 gap-3">
                              <div>
                                <label className="text-[10px] font-bold text-gray-400 uppercase tracking-wider block mb-1">Tipo</label>
                                <select value={n.tipo} onChange={(e) => updateNovedad(idx, 'tipo', e.target.value)} className="w-full px-3 py-2 rounded-lg border border-gray-300 text-sm bg-white focus:border-gray-500 transition-colors">
                                  <option value="FALTA">Falta</option>
                                  <option value="HORA_EXTRA">Hora extra</option>
                                  <option value="BONIFICACION">Bonificación</option>
                                  <option value="FESTIVO">Festivo</option>
                                  <option value="DESCUENTO_OTRO">Descuento</option>
                                </select>
                              </div>
                              <div>
                                <label className="text-[10px] font-bold text-gray-400 uppercase tracking-wider block mb-1">Cantidad</label>
                                <input type="number" min={1} value={n.cantidad} onChange={(e) => updateNovedad(idx, 'cantidad', Number(e.target.value))} className="w-full px-3 py-2 rounded-lg border border-gray-300 text-sm bg-white focus:border-gray-500 transition-colors" />
                              </div>
                              <div>
                                <label className="text-[10px] font-bold text-gray-400 uppercase tracking-wider block mb-1">Valor unitario</label>
                                <input type="number" min={0} value={n.valorUnitario} onChange={(e) => updateNovedad(idx, 'valorUnitario', Number(e.target.value))} className="w-full px-3 py-2 rounded-lg border border-gray-300 text-sm bg-white focus:border-gray-500 transition-colors" />
                              </div>
                              <div>
                                <label className="text-[10px] font-bold text-gray-400 uppercase tracking-wider block mb-1">Fecha</label>
                                <input type="date" value={n.fecha} onChange={(e) => updateNovedad(idx, 'fecha', e.target.value)} className="w-full px-3 py-2 rounded-lg border border-gray-300 text-sm bg-white focus:border-gray-500 transition-colors" />
                              </div>
                            </div>

                            <div>
                              <label className="text-[10px] font-bold text-gray-400 uppercase tracking-wider block mb-1">Descripción</label>
                              <input type="text" value={n.descripcion} onChange={(e) => updateNovedad(idx, 'descripcion', e.target.value)} placeholder="Ej: Falta por incapacidad..." className="w-full px-3 py-2 rounded-lg border border-gray-300 text-sm bg-white focus:border-gray-500 transition-colors" />
                            </div>

                            <div className="flex items-center justify-end pt-1">
                              <span className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-gray-100 text-gray-700 rounded-lg text-xs font-bold">
                                Total: {formatCurrency(n.valorUnitario * n.cantidad)}
                              </span>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>

                  {/* Observaciones */}
                  <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-5">
                    <label className="block text-xs font-semibold text-gray-700 mb-1.5">Observaciones</label>
                    <textarea value={observaciones} onChange={(e) => setObservaciones(e.target.value)} rows={2} placeholder="Notas adicionales..." className="w-full px-4 py-2.5 rounded-lg border border-gray-300 text-sm bg-white resize-none focus:border-gray-500 transition-colors" />
                  </div>
                </div>

                {/* ─── Columna derecha (1/3) ─── */}
                <div className="space-y-6">
                  <div className="bg-emerald-50 rounded-xl border border-emerald-200 p-5 sticky top-0">
                    <h3 className="text-sm font-bold text-emerald-800 uppercase tracking-wider mb-4 flex items-center gap-2">
                      <Banknote className="w-4 h-4" />
                      Resumen de Pago
                    </h3>

                    {/* Total hero */}
                    <div className="text-center p-4 bg-white rounded-xl border border-emerald-200 mb-4">
                      <p className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-1">Total a pagar</p>
                      <p className="text-3xl font-black text-emerald-700">{formatCurrency(resumen.totalPagar)}</p>
                    </div>

                    {/* Stats grid */}
                    <div className="space-y-2 mb-4">
                      <div className="flex items-center justify-between p-3 bg-white rounded-lg border border-emerald-100">
                        <div className="flex items-center gap-2">
                          <div className="p-1.5 bg-gray-100 rounded-md"><Calendar className="w-3.5 h-3.5 text-gray-500" /></div>
                          <span className="text-xs font-medium text-gray-600">Base quincenal</span>
                        </div>
                        <span className="text-sm font-bold text-gray-900">{formatCurrency(resumen.base)}</span>
                      </div>

                      <div className="flex items-center justify-between p-3 bg-white rounded-lg border border-emerald-100">
                        <div className="flex items-center gap-2">
                          <div className="p-1.5 bg-emerald-100 rounded-md"><ArrowUpCircle className="w-3.5 h-3.5 text-emerald-600" /></div>
                          <span className="text-xs font-medium text-gray-600">Bonificaciones</span>
                        </div>
                        <span className="text-sm font-bold text-emerald-700">+{formatCurrency(resumen.bonificacion)}</span>
                      </div>

                      <div className="flex items-center justify-between p-3 bg-white rounded-lg border border-emerald-100">
                        <div className="flex items-center gap-2">
                          <div className="p-1.5 bg-red-100 rounded-md"><ArrowDownCircle className="w-3.5 h-3.5 text-red-600" /></div>
                          <span className="text-xs font-medium text-gray-600">Descuentos</span>
                        </div>
                        <span className="text-sm font-bold text-red-600">-{formatCurrency(resumen.descuento)}</span>
                      </div>
                    </div>

                    {/* Devengado */}
                    <div className="pt-3 border-t border-emerald-200">
                      <div className="flex items-center justify-between">
                        <span className="text-xs font-bold text-gray-500 uppercase tracking-wider">Total devengado</span>
                        <span className="text-base font-bold text-emerald-900">{formatCurrency(resumen.devengado)}</span>
                      </div>
                    </div>
                  </div>

                  {/* Lista rápida de novedades en sidebar */}
                  {novedades.length > 0 && (
                    <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-5">
                      <h4 className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-3">Detalle novedades</h4>
                      <div className="space-y-2">
                        {novedades.map((n, idx) => {
                          const cfg = tipoNovedadConfig[n.tipo];
                          const total = n.valorUnitario * n.cantidad;
                          const isDescuento = n.tipo === 'FALTA' || n.tipo === 'DESCUENTO_OTRO';
                          return (
                            <div key={idx} className="flex items-center justify-between p-2 rounded-lg bg-gray-50 border border-gray-100">
                              <div className="flex items-center gap-2">
                                <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-bold uppercase ${cfg.bg} ${cfg.color}`}>
                                  {cfg.icon}
                                </span>
                                <span className="text-xs text-gray-600 truncate max-w-[120px]">{n.descripcion || cfg.label}</span>
                              </div>
                              <span className={`text-xs font-bold ${isDescuento ? 'text-red-600' : 'text-emerald-600'}`}>
                                {isDescuento ? '-' : '+'}{formatCurrency(total)}
                              </span>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </form>
          </div>

          {/* Footer */}
          <div className="px-6 py-4 border-t border-gray-200 bg-white">
            <div className="flex gap-3">
              <button type="button" onClick={handleClose} className="px-6 py-2.5 border border-gray-200 text-gray-700 rounded-lg hover:bg-gray-100 transition-colors font-medium">
                Cancelar
              </button>
              <button type="submit" form="nomina-form" disabled={isLoading} className="flex-1 px-6 py-2.5 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 font-medium transition-colors">
                {isLoading ? <><Loader2 className="w-5 h-5 animate-spin" /> Guardando...</> : <><CheckCircle className="w-5 h-5" /> Guardar Quincena</>}
              </button>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};
